/**
 * AuthWizardLayout — Surface Layer (/01)
 *
 * The shared login-layout shell for contained auth/onboarding wizards (sign-in,
 * security ceremony, account onboarding). Renders the SAME centered g100 card as
 * /sign-in (reuses the proven `.sign-in-page` / `.sign-in-card` classes from
 * sign-in.scss) so onboarding stops being a loose full-page surface and instead
 * opens as a contained, stepped modal-style panel.
 *
 * Zero inline styles; Carbon tokens via the reused login SCSS only.
 */

import { type ReactNode } from 'react';
import { Theme } from '@carbon/react';
import { Login } from '@carbon/icons-react';
import '../../routes/sign-in.scss';

interface AuthWizardLayoutProps {
  heading: string;
  subtitle?: string;
  /** aria id base so the card is labelled by its heading. */
  labelId?: string;
  /** Wider card for multi-step wizards (e.g. account onboarding) — the narrow sign-in
   *  width is not enough for a 4-step ProgressIndicator + a full form. */
  wide?: boolean;
  children: ReactNode;
}

export function AuthWizardLayout({ heading, subtitle, labelId = 'auth-wizard-heading', wide = false, children }: AuthWizardLayoutProps) {
  return (
    <Theme theme="g100">
      <main className="sign-in-page">
        <section className={wide ? 'sign-in-card sign-in-card--wizard' : 'sign-in-card'} aria-labelledby={labelId}>
          <Login size={32} aria-hidden="true" className="sign-in-card__icon" />
          <h1 id={labelId} className="sign-in-card__heading">
            {heading}
          </h1>
          {subtitle ? <p className="sign-in-card__subtitle">{subtitle}</p> : null}
          {children}
        </section>
      </main>
    </Theme>
  );
}
